import React from "react";
import moment from "moment-timezone";
import { connect } from "react-redux";

import BaseElem from "../../BaseElem";
import "../../Common.css";
import "./Calendar.css";
import {
  getMonths,
  getMiniWeekDays,
  firstDayOfWeek,
  areDatesSame,
  parseDate,
  newDate,
} from "../../Util/DateUtils";
import { UNDEF } from "../../Constants";
import { APP_COUNTRY } from "../../DataConstants";

type CalendarProps = {
  // This property is to set a selected date on the calendar
  selectedDate?: any;
  recipientStore?: any;

  // Callback to parent when a date is selected on the calendar
  onDateSelected?: any;

  // Callback when month changes using < > arrows
  onMonthChange?: any;

  // This will prevent selection of dates that are in past
  dontAllowPastDates?: boolean;

  // This will prevent selection of dates after today
  dontAllowFutureDates?: boolean;

  // This will disable all earlier dates
  validFirstDate?: Date;

  // disable dates selection after [X] months
  disableAfterMonths?: number;

  // When true "Today" won't be highlighted on calendar
  dontHighlightToday?: boolean;

  // Bold today is to just show today's date in bold text
  boldToday?: boolean;

  // This property is to set a selected dates on the calendar
  selectedDates?: any[];
  multiSelection?: boolean;

  // dates array to show dots under (required format: 2019-11-21T21:00:00.000Z)
  dots?: any[];
};

type CalendarState = {
  _month: any;
};

class Calendar extends BaseElem<CalendarProps, CalendarState> {
  _isMounted = false;
  _trackStart = 0;
  _prevX = 0;
  _prevY = 0;
  _firstDayOfWeek = firstDayOfWeek();
  _weekendStart = 5;
  _monthNames = getMonths();
  _todayDate = newDate();
  _circles: any = {};

  constructor(props: CalendarProps) {
    super(props);
    this.state = {
      // Currently displayed month data
      _month: { days: [] },
    };
  }

  componentDidMount() {
    const self = this,
      props = self.props;
    self._isMounted = true;
    self._loadMonth(props.selectedDates || [props.selectedDate || new Date()]);
  }
  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(oldProps: CalendarProps) {
    const self = this,
      props = self.props;

    // single selection
    if (!props.multiSelection) {
      const oldDate = oldProps.selectedDate;
      const newDate = props.selectedDate;

      const oldDots = oldProps.dots;
      const newDots = props.dots;

      if (newDate && !areDatesSame(oldDate, newDate)) {
        self.setSelectedDates([newDate]);
      } else if (oldDots !== newDots) {
        self._incrementMonthBy(0);
      }
    }
    // multi selection
    else {
      const oldDates = oldProps.selectedDates || [];
      const newDates = props.selectedDates || [];
      const len = oldDates.length;
      if (len !== newDates.length) {
        self.setSelectedDates(newDates);
      } else {
        for (let i = len; i--;) {
          const od = oldDates[i],
            nd = newDates[i];
          if (!od || !nd || !areDatesSame(od, nd)) {
            self.setSelectedDates(newDates);
            break;
          }
        }
      }
    }
  }

  getRegion = () => {
    const { recipientStore } = this.props;
    const timezone = recipientStore?.careRecipient?.cr?.timezone;
    let region = timezone?.timezones?.[APP_COUNTRY];
    if (timezone?.is_single_tz === false) {
      const city = recipientStore?.careRecipient?.co?.address_list?.[0]?.city;
      region = timezone.timezones[city];
    }
    return region;
  };

  render() {
    const self = this;

    return (
      <div
        ref="main"
        className="no-select calendar horizontal"
        onTouchStart={(e) => self._onTrack("start", e)}
        onTouchEnd={(e) => self._onTrack("end", e)}
        onMouseDown={(e) => self._onTrack("start", e)}
        onMouseUp={(e) => self._onTrack("end", e)}
      >
        <div className="cal-month">
          <div className="horizontal center cal-topbar">
            <span className="cal-month-name grow singleline">
              {self.state._month.title}
            </span>
            <img
              className="arrow cal-arrow"
              alt="Next Month"
              src="/cal-left-arrow.svg"
              onClick={() => self._onPrev()}
            />
            <img
              className="arrow cal-arrow end"
              alt="Previous Month"
              src="/cal-left-arrow.svg"
              onClick={() => self._onNext()}
            />
          </div>

          <div className="horizontal wrap cal-cells" ref="month">
            {getMiniWeekDays().map((item, idx) => (
              <div key={item + idx} className="cal-day-label">
                {self._weekDayName(item, idx)}
              </div>
            ))}

            {self.state._month.days.map((day: any, dayidx: number) => (
              <i
                key={"idx" + dayidx}
                className={self._getState(day.state)}
                onClick={() => self._onDayClick(day)}
              >
                {day.num}
              </i>
            ))}
          </div>
        </div>
      </div>
    );
  }

  resetCalendar() {
    this._loadMonth();
  }

  // Return correct week name by offsetting _firstDayOfWeek
  _weekDayName(_: any, idx: number): string {
    return getMiniWeekDays()[(this._firstDayOfWeek + idx) % 7];
  }

  setSelectedDate(selectedDate: Date) {
    this.setSelectedDates([selectedDate]);

    //const self = this;
    //self.selectedDate = selectedDate;
    //selectedDate && self.async(() => self._loadMonth([selectedDate]));
  }

  setSelectedDates(selectedDates: Date[]) {
    selectedDates && this._loadMonth(selectedDates);
  }

  _copy(date: any): Date | null {
    return date ? new Date(date) : null;
  }

  // Callback when a day is clicked
  _onDayClick(day: any) {
    const self = this;
    const month = self.state._month;

    // ignore empty cells
    if (day.num === "") return;

    const monthStart = month.monthStart;
    const date = self._copy(monthStart);
    if (date) {
      date.setDate(day.num);

      // outside callback
      self.props.onDateSelected(date);

      self.async(() => self._update(month));
    }
  }

  _loadMonth(dates: Date[] = []) {
    const self = this;
    const prevMonth = self.state._month || {};
    const prevMonthStart = prevMonth.monthStart;

    const today = newDate();
    today.setHours(0, 0, 0, 0);
    self._todayDate = today;

    let startDate;

    // Find out correct month to load based on selected dates
    if (
      prevMonthStart &&
      dates.length > 1 &&
      dates.find(
        (d: Date) =>
          d.getMonth() === prevMonthStart.getMonth() &&
          d.getFullYear() === prevMonthStart.getFullYear()
      )
    ) {
      startDate = prevMonthStart;
    } else {
      startDate = dates[0];
    }

    if (startDate === prevMonthStart) {
      self._update(prevMonth);
      return false;
    }

    const monthStart = self._copy(startDate || today);
    if (!monthStart) return false;

    monthStart.setDate(1);
    const month = monthStart.getMonth(),
      year = monthStart.getFullYear();

    const monthObj: any = {
      title: self._monthNames[month] + " " + year,
      monthStart,
      days: [],
    };

    let validFirstDay = monthStart.getDay() - self._firstDayOfWeek;
    if (validFirstDay < 0) validFirstDay += 7;
    for (let k = 0; k < validFirstDay; k++) {
      monthObj.days.push({ num: "", state: "" });
    }

    let remainingLength = validFirstDay;
    const numDays = self._daysInMonth(monthStart);
    for (let l = 1; l <= numDays; l++) {
      monthObj.days.push({ num: l, state: "" });
      if (++remainingLength === 7) {
        remainingLength = 0;
      }
    }

    while (remainingLength > 0 && remainingLength++ < 7) {
      monthObj.days.push({ num: "", state: "" });
    }

    // update styles
    self._update(monthObj);
    return true;
  }

  // update css styles
  _update(monthData: any) {
    const self = this,
      props = self.props;
    if (!monthData) return;

    self._setCircles(props.dots);

    const TYPE_SELECTED = "s";
    const TYPE_TODAY = "t";
    const TYPE_TODAY_BOLD = "tb";
    const TYPE_DISABLED = "d";

    const selectedDates = props.multiSelection
      ? props.selectedDates || []
      : [props.selectedDate];

    // compare day,month, year with date object
    // return 0 if same, -1 when smaller, +1 when greater than date
    const compare = (
      d1: number,
      m1: number,
      y1: number,
      date: Date
    ): number => {
      const d2 = date.getDate(),
        m2 = date.getMonth(),
        y2 = date.getFullYear();
      if (y1 === y2 && m1 === m2 && d1 === d2) return 0;
      if (
        y1 > y2 ||
        (y1 === y2 && m1 > m2) ||
        (y1 === y2 && m1 === m2 && d1 > d2)
      )
        return 1;
      return -1;
    };
    //var z = (n:number) => (n < 10 ? "0" : "") + n;

    const monthStart = monthData.monthStart;
    const days = monthData.days;
    const startMonth = monthStart.getMonth();
    const startYear = monthStart.getFullYear();

    const lastValidDate = props.disableAfterMonths && new Date();
    lastValidDate &&
      lastValidDate.setMonth(
        lastValidDate.getMonth() + props.disableAfterMonths!
      );

    const validFirstDate = props.validFirstDate;

    for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
      const day = days[dayIndex];
      const curNum = day.num;
      let newState = "";

      if (
        props.dontAllowPastDates &&
        compare(curNum, startMonth, startYear, self._todayDate) < 0
      ) {
        newState = TYPE_DISABLED;
      } else if (
        props.dontAllowFutureDates &&
        compare(curNum, startMonth, startYear, self._todayDate) > 0
      ) {
        newState = TYPE_DISABLED;
      } else if (
        validFirstDate &&
        compare(curNum, startMonth, startYear, validFirstDate) < 0
      ) {
        newState = TYPE_DISABLED;
      } else if (
        lastValidDate &&
        compare(curNum, startMonth, startYear, lastValidDate) > 0
      ) {
        newState = TYPE_DISABLED;
      } else {
        for (let k = selectedDates.length; k--;) {
          const selectedDate = selectedDates[k];

          // Highlight selected date if any
          if (
            selectedDate &&
            !compare(curNum, startMonth, startYear, selectedDate)
          ) {
            newState = TYPE_SELECTED;
            break;
          }
        }
      }

      if (
        !props.dontHighlightToday &&
        !compare(curNum, startMonth, startYear, self._todayDate)
      ) {
        newState = props.boldToday
          ? newState + " " + TYPE_TODAY_BOLD
          : TYPE_TODAY;
      }

      if (self._circles[[startYear, startMonth, curNum].join("-")]) {
        newState = (newState ? newState + " " : "") + "dot";
      }

      monthData.days[dayIndex].state = newState;
    }

    self._isMounted && self.setState(() => ({ _month: monthData }));
  }

  // set dots
  _setCircles(dotDates?: any[]) {
    const self = this;
    if (!dotDates) return;
    self._circles = {};
    for (let k = dotDates && dotDates.length; k--;) {
      const d = moment.tz(parseDate(dotDates[k], true), this.getRegion());
      self._circles[
        [d.format("YYYY"), Number(d.format("M")) - 1, d.format("D")].join("-")
      ] = true;
    }
  }

  _daysInMonth(d: Date): number {
    //month 1 based
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  }

  _getState(state: any): any {
    return !state ? UNDEF : state;
  }

  // Move calendar to next month
  _onNext() {
    this._incrementMonthBy(1);
    this._playAnim(false);
  }

  // Move calendar to previous month
  _onPrev() {
    this._incrementMonthBy(-1);
    this._playAnim(true);
  }

  _incrementMonthBy(val: number) {
    const self = this,
      month = self.state._month;
    const startDate = self._copy(month.monthStart);
    if (startDate) {
      startDate.setMonth(startDate.getMonth() + val);
      self._loadMonth([startDate]);

      if (val) {
        const monthChangeCallback = self.props.onMonthChange;
        monthChangeCallback && monthChangeCallback(startDate);
      }
    }
  }

  _onTrack(type: string, e: any) {
    const self = this;
    let touch =
      (e.targetTouches && e.targetTouches[0]) ||
      (e.changedTouches && e.changedTouches[0]);
    let x = (touch || e).clientX;
    let y = (touch || e).clientY;
    const MAX_TRACK_TIME = touch ? 250 : 500; //max milliseconds to finish the swipe

    switch (type) {
      case "start":
        self._trackStart = new Date().getTime();
        self._prevX = x;
        self._prevY = y;

        break;
      case "end":
        {
          const X_THRESHOLD = 70;
          const dx = x - self._prevX,
            dy = y - self._prevY;
          const ySatisfied = Math.abs(dy) < 40 || Math.abs(dx / dy) > 3;

          if (
            ySatisfied &&
            new Date().getTime() - self._trackStart < MAX_TRACK_TIME
          ) {
            //swipe left
            if (dx < -X_THRESHOLD) {
              self._onNext();
            }

            // swipe right
            else if (dx > X_THRESHOLD) {
              self._onPrev();
            }
          }
          self._trackStart = 0;
        }
        break;
      default:
        break;
    }
  }

  // play fake swipe animation
  _playAnim(invert = false) {
    const self = this,
      month: any = self.refs.month,
      style = month.style;
    const transform = "transform",
      opacity = "opacity",
      transition = "transition";
    const mtx = "translateX(-10%)",
      tx = mtx.replace("-", ""),
      duration = "0.08s ease-in-out";

    style[transform] = invert ? tx : mtx;
    style[opacity] = 0;
    style[transition] = duration;
    style.transitionProperty = transform + "," + opacity;

    self.async(() => {
      style[transition] = "";
      style[transform] = invert ? mtx : tx;

      self.async(() => {
        style[transition] = duration;
        style[opacity] = 1;
        style[transform] = "";
      }, 50);
    }, 90);
  }
}

const mapStateToProps = (state: any) => {
  const { recipientStore } = state;
  return {
    ...state,
    crList: recipientStore.crList,
  };
};

const mapDispatchToProps = (_dispatch: any) => ({});

export default connect<any, any>(mapStateToProps, mapDispatchToProps)(Calendar);
